/**
 * Obstruction Details Header
 *
 * Title block rendered above the section content in the obstruction details
 * panel. Mirrors `TaskDetailsHeader` — status dot + title + (optional) road
 * + coordinates + type/severity badges row. Status menu / Edit / Delete
 * actions are published to the overlay toolbar via `useRegisterDetailSlots`
 * and live outside the header.
 */
import { memo, type CSSProperties } from 'react';
import clsx from 'clsx';
import { Badge, Group, Stack, Text } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FONT_WEIGHT_SEMIBOLD, ICON_SIZE_XS } from '@/constants/ui';
import {
  OBSTRUCTION_STATUS_COLORS,
  SEVERITY_COLORS,
} from '@/constants/obstructions';
import { formatCoordinates } from '@/utils/helpers';

import type { ObstructionDetailsHeaderProps } from './ObstructionDetailsHeader.types';
import styles from './ObstructionDetailsHeader.module.css';

export const ObstructionDetailsHeader = memo(function ObstructionDetailsHeader({
  obstruction,
  className,
}: ObstructionDetailsHeaderProps) {
  const { t } = useTranslation();
  const { anchorPoint, location, title, status, type, severity } = obstruction;

  const statusStyle = {
    '--obstruction-status-color': OBSTRUCTION_STATUS_COLORS[status],
  } as CSSProperties;

  const severityStyle = {
    '--obstruction-severity-color': SEVERITY_COLORS[severity],
  } as CSSProperties;

  return (
    <Stack gap="2xs" className={clsx(styles.titleStack, className)}>
      <div className={styles.titleRow}>
        <span
          className={styles.statusDot}
          style={statusStyle}
          aria-label={t(`obstructions.status.${status}`)}
        />
        <Text
          size="lg"
          fw={FONT_WEIGHT_SEMIBOLD}
          lineClamp={2}
          className={styles.title}
        >
          {title}
        </Text>
      </div>

      <Group gap="2xs" className={styles.locationGroup}>
        <IconMapPin size={ICON_SIZE_XS} className={styles.locationIcon} />
        {location !== null && location.length > 0 ? (
          <>
            <Text size="xs" c="dimmed" className={styles.roadName}>
              {location}
            </Text>
            <Text
              size="xs"
              c="dimmed"
              ff="monospace"
              className={styles.coordinates}
            >
              {formatCoordinates(anchorPoint.lat, anchorPoint.lng)}
            </Text>
          </>
        ) : (
          <Text
            size="xs"
            c="dimmed"
            ff="monospace"
            className={styles.coordinates}
          >
            {formatCoordinates(anchorPoint.lat, anchorPoint.lng)}
          </Text>
        )}
      </Group>

      <div className={styles.badgesRow}>
        <Badge size="sm" variant="light" color="gray">
          {t(`obstructions.type.${type}`)}
        </Badge>
        <Badge
          size="sm"
          variant="filled"
          className={styles.severityBadge}
          style={severityStyle}
        >
          {t(`obstructions.severity.${severity}`)}
        </Badge>
      </div>
    </Stack>
  );
});
